/**
 * The hang space.
 *
 * A fitting rail, not a basket: "+" adds a piece to the Shopify cart and the
 * piece stays on the rail. Only "×" takes it off.
 *
 * Contents persist in localStorage under ss_rail so the rail survives
 * navigation — the theme is multi-page, so this is the only way the rail can
 * feel continuous.
 */

/* Inlined rather than imported — see the note at the top of mode.js. */
const getMode = () =>
  document.documentElement.classList.contains('mode-immersive') ? 'immersive' : 'standard';

const KEY = 'ss_rail';
const rail = document.querySelector('[data-rail]');

if (rail) {
  const panel = rail.querySelector('[data-rail-panel]');
  const list = rail.querySelector('[data-rail-items]');
  const countEl = rail.querySelector('[data-rail-count]');
  const toggle = rail.querySelector('[data-rail-toggle]');
  const closeBtn = rail.querySelector('[data-rail-close]');
  const flyer = document.querySelector('[data-rail-flyer]');

  let items = read();
  let hoverTimer = null;

  function read() {
    try {
      const parsed = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write() {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* private browsing — the rail works for this session only */
    }
  }

  const money = (cents) =>
    (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP'
    });

  /* ---- Rendering ------------------------------------------ */

  function render({ newVariantId = null } = {}) {
    list.textContent = '';

    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'rail__item';
      if (item.variantId === newVariantId) li.classList.add('is-new');
      li.dataset.variantId = item.variantId;

      const thumb = document.createElement('a');
      thumb.className = 'rail__thumb';
      thumb.href = `${window.Shopify?.routes?.root || '/'}products/${item.handle}`;
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
      }

      const meta = document.createElement('div');
      meta.className = 'rail__meta';

      const name = document.createElement('span');
      name.className = 'rail__name';
      name.textContent = item.title;
      meta.appendChild(name);

      if (item.variantTitle) {
        const variant = document.createElement('span');
        variant.className = 'rail__variant';
        variant.textContent = item.variantTitle;
        meta.appendChild(variant);
      }

      const controls = document.createElement('div');
      controls.className = 'rail__controls';

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'rail__btn';
      add.dataset.railAdd = '';
      add.textContent = '+';
      add.setAttribute('aria-label', `Add ${item.title} to cart`);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'rail__btn';
      remove.dataset.railRemove = '';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${item.title} from the hang space`);

      controls.append(add, remove);
      meta.appendChild(controls);
      li.append(thumb, meta);
      list.appendChild(li);
    });

    const count = items.length;
    rail.classList.toggle('has-items', count > 0);
    countEl.hidden = count === 0;
    countEl.textContent = count ? String(count) : '';
    toggle.setAttribute(
      'aria-label',
      count ? `Hang space, ${count} ${count === 1 ? 'piece' : 'pieces'}` : 'Hang space, empty'
    );

    // Contents changed, so whether the bar overflows may have changed too.
    // Hoisted; runs after the panel has had a frame to lay out.
    window.setTimeout(syncArrows, 0);
  }

  /* ---- Open and close ------------------------------------- */

  /**
   * The open panel grows upward until its top meets the top of the first nav
   * item. That is a relationship between two fixed elements, so it cannot be
   * expressed in CSS alone — the nav is centred on a percentage of viewport
   * height and its size depends on the menu. Measured here and published as
   * --rail-open-height; the stylesheet falls back to 42vh until it lands.
   */
  function measureOpenHeight() {
    const firstNavItem = document.querySelector('.nav__item');
    if (!firstNavItem) return;
    const top = firstNavItem.getBoundingClientRect().top;
    if (top <= 0) return;
    rail.style.setProperty('--rail-open-height', `${Math.round(window.innerHeight - top)}px`);
  }

  /**
   * Thumbnail sizing.
   *
   * A hovered piece reaches a third of the hang space; the rest sit at a
   * smaller resting size. Both are derived from the panel's measured height
   * rather than hardcoded, so the proportion holds at any viewport.
   */
  const THUMB_MAX_RATIO = 1 / 3;
  const THUMB_MIN_RATIO = 0.45;   // of max — still comfortably larger than a plain thumbnail
  const PROXIMITY_ITEMS = 1.8;   // how many slots either side feel the pull

  function measureThumbs() {
    const openHeight = parseFloat(rail.style.getPropertyValue('--rail-open-height'))
      || window.innerHeight * 0.42;
    const barHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rail-bar')) || 44;

    // The panel also carries a heading, the item's name and its controls, so
    // the thumbnail gets a third of the panel rather than a third of everything.
    const panelHeight = Math.max(160, openHeight - barHeight);
    const max = Math.round(panelHeight * THUMB_MAX_RATIO);
    const min = Math.round(max * THUMB_MIN_RATIO);

    rail.style.setProperty('--thumb-max', `${max}px`);
    rail.style.setProperty('--thumb-min', `${min}px`);
    /* Growth is a transform, so the sizes have to reach CSS as a scale too. */
    rail.style.setProperty('--thumb-scale-max', (max / min).toFixed(3));
    return { min, max };
  }

  let thumbSizes = { min: 96, max: 200 };

  function measureAll() {
    measureOpenHeight();
    thumbSizes = measureThumbs();
  }

  measureAll();
  window.addEventListener('resize', measureAll);
  /* Immersive mode grows nav items on hover, which changes where the top sits. */
  document.addEventListener('ss:modechange', () => {
    measureAll();
    if (getMode() !== 'immersive') resetThumbs();
  });

  /* ---- Proximity sizing, the same curve as the right-edge nav ---- */

  const canBeImmersive = () =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Cubed proximity, not eased-out: easeOutCubic saturates, so everything
     near the pointer reads at nearly full size and the row swells as a block
     instead of responding to where the pointer actually is. */
  const falloff = (t) => t * t * t;

  function resetThumbs() {
    list.querySelectorAll('.rail__item').forEach((el) => el.style.removeProperty('--thumb-scale'));
  }

  /**
   * Influence is computed against each piece's RESTING slot, not its current
   * box. Sizing a flex row changes where its items are, so measuring live
   * geometry feeds the effect back into its own input: the peak drifts off the
   * pointer and the curve comes out lopsided. Resting slots are fixed, so the
   * piece under the pointer reliably reaches full size.
   */
  function sizeFromPointer(pointerX) {
    const items = [...list.querySelectorAll('.rail__item')];
    if (!items.length) return;

    const { min, max } = thumbSizes;
    const scaleMax = max / min;

    // Slots are fixed at the largest a piece can get, so this stride is exact
    // and, unlike a live measurement, cannot be disturbed by the sizing itself.
    const slot = max * 0.8;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 18;
    const stride = slot + gap;

    // Purely horizontal: the pieces are listed along the bar, so only the
    // pointer's x position decides which one is being reached for.
    const listBox = list.getBoundingClientRect();
    const pointerIndex = (pointerX - listBox.left + list.scrollLeft - slot / 2) / stride;

    items.forEach((el, i) => {
      const distance = Math.abs(i - pointerIndex);
      const influence = distance >= PROXIMITY_ITEMS ? 0 : falloff(1 - distance / PROXIMITY_ITEMS);
      el.style.setProperty('--thumb-scale', (1 + (scaleMax - 1) * influence).toFixed(3));
    });
  }

  list.addEventListener('pointermove', (event) => {
    if (getMode() !== 'immersive' || !canBeImmersive()) return;
    if (event.pointerType === 'touch') return;
    sizeFromPointer(event.clientX);
  }, { passive: true });

  list.addEventListener('pointerleave', resetThumbs);

  /* ---- Consent banner ------------------------------------- */

  /**
   * Shopify's cookie banner is fixed to the bottom of the viewport at
   * z-index 2000000, which is exactly where this rail lives. Raising the rail
   * above it would be wrong — a consent decision should not be coverable — so
   * the rail steps up by the banner's height instead, and drops back down once
   * the visitor has answered.
   */
  function syncConsentOffset() {
    /* Not offsetParent: that is null for any position:fixed element, which the
       banner is, so it would always read as hidden. Measure the box instead. */
    const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner__dialog');
    let height = 0;
    if (banner) {
      const box = banner.getBoundingClientRect();
      const visible = box.height > 0 && getComputedStyle(banner).display !== 'none';
      // Only what actually overlaps the bottom of the viewport pushes the rail.
      if (visible) height = Math.ceil(Math.max(0, window.innerHeight - box.top));
    }
    rail.style.setProperty('--rail-offset', `${height}px`);
    // The panel's available height changes with it.
    measureOpenHeight();
    thumbSizes = measureThumbs();
  }

  syncConsentOffset();
  window.addEventListener('resize', syncConsentOffset);

  /* The banner is injected asynchronously, and answering it may either remove
     the element or just hide it — and it does not always sit where you'd
     expect in the DOM. So watch the whole subtree including attribute changes,
     debounced, rather than trying to guess which of those Shopify does. */
  if ('MutationObserver' in window) {
    let pending = null;
    const observer = new MutationObserver(() => {
      window.clearTimeout(pending);
      pending = window.setTimeout(syncConsentOffset, 150);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
  }

  /* Belt and braces: any click could be the one that answers the banner. */
  document.addEventListener('click', () => window.setTimeout(syncConsentOffset, 250), { passive: true });

  /* ---- Overflow arrows ------------------------------------- */

  const prevBtn = rail.querySelector('[data-rail-prev]');
  const nextBtn = rail.querySelector('[data-rail-next]');

  /** Shown only when the pieces overrun the bar, and disabled at each end. */
  function syncArrows() {
    if (!prevBtn || !nextBtn) return;
    const overflowing = list.scrollWidth > list.clientWidth + 1;

    prevBtn.hidden = !overflowing;
    nextBtn.hidden = !overflowing;
    if (!overflowing) return;

    const maxScroll = list.scrollWidth - list.clientWidth;
    prevBtn.disabled = list.scrollLeft <= 1;
    nextBtn.disabled = list.scrollLeft >= maxScroll - 1;
  }

  function step(direction) {
    const slot = thumbSizes.max * 0.8;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 18;
    list.scrollBy({ left: direction * (slot + gap) * 2, behavior: 'smooth' });
  }

  prevBtn?.addEventListener('click', () => step(-1));
  nextBtn?.addEventListener('click', () => step(1));
  list.addEventListener('scroll', syncArrows, { passive: true });
  window.addEventListener('resize', syncArrows);

  function open() {
    measureOpenHeight();
    // The list has no measurable width until the panel is open.
    window.setTimeout(syncArrows, 0);
    rail.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
  }

  function close() {
    rail.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
  }

  const isOpen = () => rail.classList.contains('is-open');

  toggle.addEventListener('click', () => (isOpen() ? close() : open()));
  if (closeBtn) closeBtn.addEventListener('click', close);

  /* Hovering the edge wakes it; leaving the whole rail closes it after a beat
     so the pointer can travel from edge to panel without it snapping shut. */
  rail.addEventListener('pointerenter', (event) => {
    if (getMode() !== 'immersive') return;
    if (event.pointerType === 'touch') return;
    window.clearTimeout(hoverTimer);
    open();
  });

  rail.addEventListener('pointerleave', (event) => {
    if (getMode() !== 'immersive') return;
    if (event.pointerType === 'touch') return;
    window.clearTimeout(hoverTimer);
    hoverTimer = window.setTimeout(close, 320);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) {
      close();
      toggle.focus();
    }
  });

  /* The "Hang space" nav item. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-hang-open]');
    if (!opener) return;
    event.preventDefault();
    open();
    panel.querySelector('a, button')?.focus({ preventScroll: true });
  });

  /* ---- Item controls -------------------------------------- */

  list.addEventListener('click', async (event) => {
    const li = event.target.closest('.rail__item');
    if (!li) return;
    const variantId = Number(li.dataset.variantId);

    if (event.target.closest('[data-rail-remove]')) {
      items = items.filter((item) => item.variantId !== variantId);
      write();
      render();
      return;
    }

    const addBtn = event.target.closest('[data-rail-add]');
    if (!addBtn) return;

    addBtn.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('add failed');

      // Stays on the rail — this is a fitting rail, not the cart.
      addBtn.dataset.state = 'done';
      addBtn.textContent = '✓';
      window.setTimeout(() => {
        delete addBtn.dataset.state;
        addBtn.textContent = '+';
      }, 1400);

      document.dispatchEvent(new CustomEvent('ss:cartupdate', { detail: { open: true } }));
    } catch (e) {
      addBtn.textContent = '!';
      window.setTimeout(() => { addBtn.textContent = '+'; }, 1400);
    } finally {
      addBtn.disabled = false;
    }
  });

  /* ---- Fly to rail ---------------------------------------- */

  /**
   * Sends a thumbnail from wherever the piece was to the rail edge.
   * Uses the Web Animations API rather than GSAP: it is a single tween of
   * transform and opacity, so pulling in 70kB for it would be indefensible.
   */
  function flyToRail(origin, image) {
    if (!flyer || !origin) return Promise.resolve();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();

    const from = origin.getBoundingClientRect();
    const edge = toggle.getBoundingClientRect();

    flyer.style.backgroundImage = image ? `url("${image}")` : '';
    flyer.classList.add('is-flying');

    const startX = from.left + from.width / 2 - 28;
    const startY = from.top + from.height / 2 - 35;
    const endX = edge.left + edge.width / 2 - 28;
    const endY = edge.top + edge.height / 2 - 35;

    const animation = flyer.animate(
      [
        { transform: `translate(${startX}px, ${startY}px) scale(1.6)`, opacity: 0.9 },
        { transform: `translate(${endX}px, ${endY}px) scale(0.5)`, opacity: 0 }
      ],
      { duration: 620, easing: 'cubic-bezier(0.5, 0, 0.2, 1)', fill: 'forwards' }
    );

    return animation.finished
      .catch(() => {})
      .then(() => flyer.classList.remove('is-flying'));
  }

  /* ---- Adding ---------------------------------------------- */

  let closeTimer = null;

  /**
   * State first, animation second.
   *
   * The flight is decorative and must never gate the rail's contents: an
   * animation that is interrupted, unsupported or throttled (a background tab
   * suspends WAAPI) would otherwise leave the rail silently out of date while
   * localStorage says the piece is hanging.
   */
  function hang(item, origin) {
    // Another tab or page may have written since we last read.
    items = read();

    const already = items.some((entry) => entry.variantId === item.variantId);
    if (!already) {
      items.push(item);
      write();
    }

    render({ newVariantId: already ? null : item.variantId });
    open();

    if (!already) flyToRail(origin, item.image);

    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(close, 1800);

    return !already;
  }

  /* product-form.js and drag-hang.js both announce through this event. */
  document.addEventListener('ss:hang', (event) => {
    hang(event.detail.item, event.detail.origin);
  });

  /* Another tab changed the rail. */
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    items = read();
    render();
  });

  window.SSHang = { hang, open, close, read };

  render();
}
